/**
 * Modal. A SHARED, presentation-only centered modal shell: a backdrop plus a
 * centered card with a close (X) button and a header (eyebrow + title).
 * Backdrop click and Escape close it. Extracted from NotesModal (COCKPIT.md
 * section 2.10) so the same centered-overlay chrome can back other modals
 * (e.g. ApiErrorDetailModal) without re-implementing the backdrop/Escape/close
 * wiring on every one. NotesModal itself is refactored to render its
 * tabs/notes/audit body as this component's children.
 */

import { useEffect, type ReactNode } from 'react';
import { X } from 'lucide-react';

export type ModalProps = {
  /** Heading shown in the modal header. */
  title: string;
  /** Optional eyebrow caption above the title (e.g. "Order", "API errors"). */
  eyebrow?: string;
  /** Closes the modal. Wired to the backdrop, the X button, and Escape. */
  onClose: () => void;
  children: ReactNode;
  /** Tailwind max-width class for the card. Defaults to the width the notes
   * modal has always used; a data-heavy modal (e.g. a detail table) can pass
   * a wider one. */
  widthClassName?: string;
};

export function Modal({ title, eyebrow, onClose, children, widthClassName = 'max-w-2xl' }: ModalProps) {
  // Escape closes the modal.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4"
      role="presentation"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
        className={`relative w-full ${widthClassName} rounded-lg border border-amp-line bg-amp-white shadow-2 dark:border-amp-dark-line dark:bg-amp-dark-surface`}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 text-amp-ink-muted hover:text-amp-ink dark:text-amp-dark-ink-muted dark:hover:text-amp-dark-ink"
          aria-label="Close"
        >
          <X className="h-4 w-4" aria-hidden />
        </button>

        <div className="border-b border-amp-line px-5 pt-5 pb-3 dark:border-amp-dark-line">
          {eyebrow && (
            <p className="text-xs uppercase tracking-wide text-amp-olive dark:text-amp-olive-dark">
              {eyebrow}
            </p>
          )}
          <h2 className="text-lg font-semibold text-amp-ink dark:text-amp-dark-ink">{title}</h2>
        </div>

        {children}
      </div>
    </div>
  );
}
